/**
 * Provides chat/message identity to deeply-nested chat children — primarily
 * the static markdown-component bindings (`mdComponents.a`, `evidenceMdComponents.a`)
 * that cannot accept React props directly.
 *
 * EVI-1486: introduced so source/citation click tracking can attach
 * `chat_id` + `message_id` to `evidence_source_clicked` from any surface
 * inside an assistant response, without prop-drilling through Streamdown.
 *
 * Components rendered outside an assistant message read the default value
 * (no chat/message context) and tracking events simply omit the IDs.
 */
import { createContext, useContext } from 'react';

import type {
  EvidenceSourceInteraction,
  EvidenceSourceSurface,
} from '@/models/analytics/features/evidence';

export interface ChatMessageContextValue {
  /** Backend chat ID for the assistant response being rendered, when known. */
  chatId?: string;
  /** Backend message ID (msg_*) of the assistant response, when known. */
  messageId?: string;
  /** Scribe session ID, when the chat is rendered inside a session. */
  sessionId?: string;
  /**
   * Default visual surface to attach to inline-element click tracking
   * (e.g. `inline_markdown_link` for streamed `<a>` tags).
   * Components that fire from a different surface should override this
   * explicitly when emitting their event.
   */
  defaultSurface?: EvidenceSourceSurface;
  /** Optional default `interaction` value, mirrors `defaultSurface`. */
  defaultInteraction?: EvidenceSourceInteraction;
}

const EMPTY_CONTEXT: ChatMessageContextValue = {};

export const ChatMessageContext = createContext<ChatMessageContextValue>(EMPTY_CONTEXT);

export const useChatMessageContext = (): ChatMessageContextValue =>
  useContext(ChatMessageContext);
